import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionTypesOptions } from 'vopay';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import { useToast } from '@/providers/Toast.provider';

type UseFundedFromLabelParams = {
  parentType?: TransactionTypesOptions;
  // Callers aren't consistent. keep it string
  currency: string;
};

// "Funded From" row shown on the Transaction Details card whenever this transaction was
// pre-funded — mirrors TransactionConfirmationPage.tsx's fundedFrom, since both surfaces
// present the same information from the same Confluence spec.
//
// Unscoped (no keyPrefix): this is shared across several *Info.tsx files, each with its own
// keyPrefix one level deeper than where these FundedFrom keys live, so a scoped `t` here would
// resolve the wrong path in every caller. Callers should read `fundedFromLabel` for the row's
// label instead of translating it themselves.
export default function useFundedFromLabel({ parentType, currency }: UseFundedFromLabelParams) {
  const { t } = useTranslation('translations');
  const toast = useToast();
  const { fundingOptions, fetchFundingOptions } = useFetchFundingOptions();

  useEffect(() => {
    if (!parentType) return;

    (async () => {
      try {
        await fetchFundingOptions({ Currency: currency });
      } catch {
        toast.neutral(
          t('Transaction.TransactionHistory.Details.FundedFrom.fetchError', {
            defaultValue: 'Could not load funding source details',
          }),
        );
      }
    })();
  }, [parentType, currency]);

  const fundedFromValue = useMemo(() => {
    if (parentType === 'Credit Card') {
      const creditOption = fundingOptions.find((option) => option.Source === 'credit');
      return t('Transaction.TransactionHistory.Details.FundedFrom.creditCard', {
        cardNumber: creditOption?.CreditCardNumber || '',
        defaultValue: 'Credit Card {{cardNumber}}',
      });
    }

    if (parentType === 'EFT Funding' || parentType === 'ACH Funding') {
      const bankOption = fundingOptions.find((option) => option.Source === 'bank');
      return t('Transaction.TransactionHistory.Details.FundedFrom.bankAccount', {
        accountNumber: bankOption?.AccountNumber || '',
        defaultValue: 'Bank Account {{accountNumber}}',
      });
    }

    return undefined;
  }, [parentType, fundingOptions, t]);

  const fundedFromLabel = t('Transaction.TransactionHistory.Details.FundedFrom.label', {
    defaultValue: 'Funded From',
  });

  return { fundedFromValue, fundedFromLabel };
}
